/**
 * Socket Context
 *
 * Provides Socket.io connections across the application
 */

import { useSocket } from "@/hooks/useSocket";
import {
  NotificationEventData,
  SocketEvent,
  SocketEvents,
  SocketNamespaces,
} from "@/interfaces/ISocket";
import api from "@/services/api";
import React, { createContext, useContext, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Socket } from "socket.io-client";

interface SocketContextValue {
  notificationSocket: Socket | null;
  connected: {
    notifications: boolean;
  };
  connecting: {
    notifications: boolean;
  };
}

const SocketContext = createContext<SocketContextValue | undefined>(undefined);

interface SocketProviderProps {
  children: React.ReactNode;
}

export function SocketProvider({ children }: SocketProviderProps) {
  const dispatch = useDispatch();
  const notifications = useSocket(SocketNamespaces.NOTIFICATIONS, {
    onConnect: () => {
      console.log("[SocketProvider] Notifications connected");
      dispatch(
        api.util.invalidateTags([
          "SoftphoneLines",
          "CallLogs",
          "MySoftphoneLine",
        ]),
      );
    },
    onDisconnect: (reason) => {
      console.log("[SocketProvider] Notifications disconnected:", reason);
    },
    onError: (error) => {
      console.error("[SocketProvider] Notifications error:", error);
    },
  });

  // Handle notification events
  useEffect(() => {
    if (!notifications.socket) return;

    const socket = notifications.socket;

    // User notification
    socket.on(
      SocketEvents.DATA_UPDATE,
      (payload: SocketEvent<NotificationEventData>) => {
        const { tags } = payload.data;

        console.log("[Socket] New notification:", payload.data);

        dispatch(api.util.invalidateTags(tags as any));
      },
    );

    return () => {
      socket.off(SocketEvents.DATA_UPDATE);
    };
  }, [notifications.socket]);

  const value: SocketContextValue = {
    notificationSocket: notifications.socket,
    connected: {
      notifications: notifications.connected,
    },
    connecting: {
      notifications: notifications.connecting,
    },
  };

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
}

/**
 * Hook to access socket context
 */
export function useSocketContext() {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error("useSocketContext must be used within a SocketProvider");
  }
  return context;
}
